import { gql } from '@apollo/client';
import { NextPage } from 'next';
import { FunctionComponent, ReactNode, useState } from 'react';
import styled from 'styled-components';
import { FOOTER_QUERY } from '../components/footer';
import { HEADER_QUERY, Header } from '../components/header';
import { Modal } from '../components/modal';
import { Col, ResponsiveContainer, Row } from '../components/responsive';
import { Button } from '../components/ui/button';
import { ButtonGroup } from '../components/ui/buttonGroup';
import { Icon } from '../components/ui/icon';
import { Separator } from '../components/ui/separator';
import { ContentStackClient } from '../lib/contentstack';
import { Pricing, PricingPlansStarter } from '../types/contentstack';
import { IPageProps } from '../types/page';
import { classnames } from '../utils/classnames';
import { getAssetUrl } from '../utils/contentstack';
import { mediaScreen } from '../utils/mediaScreen';
import { Footer } from '../components/footer';
import Link from 'next/link';
import { Accordion } from '../components/accordion';

export const Page: NextPage<IPricingPage> = (props) => {
  return <PageComponent {...props} />;
};

const PageComponent: NextPage<IPricingPage> = ({ header, page, footer }) => {
  const [isGatewayInfoModalOpen, setIsGatewayInfoModalOpen] = useState(false);
  const [gateway, setGateway] = useState<
    'gateway.gateway_virtual' | 'gateway.gateway_physical'
  >('gateway.gateway_virtual');

  return (
    <>
      <Header
        pageTitle={page?.title}
        pageDescription={page?.description}
        {...header}
      />

      <Hero>
        <WidthDelimiter>
          <ResponsiveContainer>
            <Row>
              <Col md={8}>
                <HeroTitle
                  dangerouslySetInnerHTML={{
                    __html: page?.hero?.title ?? '',
                  }}
                />

                <HeroText>{page?.hero?.text}</HeroText>
              </Col>
            </Row>
          </ResponsiveContainer>
        </WidthDelimiter>
      </Hero>

      <WidthDelimiter>
        <ResponsiveContainer>
          <Plans>
            {['starter', 'pro', 'enterprise', 'a_la_carte'].map((p) => {
              const plan: PricingPlansStarter = (page?.plans as any)[p];

              if (!plan?.title) {
                return null;
              }

              return (
                <Plan
                  key={p}
                  title={plan.title!}
                  description={plan.text!}
                  price={plan.price!}
                  priceDetails={plan.price_details!}
                  button={
                    <Link href={plan?.button?.href!} legacyBehavior passHref>
                      <Button mType="primary">{plan?.button?.title}</Button>
                    </Link>
                  }
                />
              );
            })}
          </Plans>

          <div style={{ textAlign: 'center', marginTop: 50 }}>
            {page?.plans?.fess_disclaimer?.text && (
              <p style={{ marginBottom: 30 }}>
                {page?.plans?.fess_disclaimer?.text}
              </p>
            )}

            <Link href="/pricing-features" legacyBehavior passHref>
              <Button mType="secondary">COMPLETE FEATURE LIST</Button>
            </Link>
          </div>
        </ResponsiveContainer>
      </WidthDelimiter>

      <WidthDelimiter>
        <ResponsiveContainer>
          <GatewayCard>
            <div style={{ flex: 3 }}>
              <h2 className="title">{page?.gateway?.title}</h2>
              <p className="text">{page?.gateway?.text}</p>
            </div>

            <div>
              <h3 className="subtitle">
                {page?.gateway?.types_title}
                <Icon
                  name="info-circle"
                  size={18}
                  onClick={() => setIsGatewayInfoModalOpen(true)}
                />
              </h3>

              <ButtonGroup
                selectedOption={gateway}
                disabled={false}
                onSelectOption={(o) => setGateway(o.id as any)}
                options={page?.gateway?.types_info?.types?.map((type) => ({
                  title: type?.title!,
                  id: type?.slug!,
                }))}
              />
            </div>

            <div style={{ flex: 1.5 }}>
              <h3 className="subtitle" style={{ opacity: 0 }}>
                Price
              </h3>

              <h4 className="price">
                {gateway === 'gateway.gateway_physical'
                  ? '$1000'
                  : '$250 / Month'}
              </h4>
            </div>
          </GatewayCard>

          <Modal
            isOpen={isGatewayInfoModalOpen}
            onRequestClose={() => setIsGatewayInfoModalOpen(false)}
          >
            <GatewayInfoModalContent>
              <h2>{page?.gateway?.types_info?.title}</h2>
              <p>{page?.gateway?.types_info?.text}</p>

              <Icon
                name="close"
                size={14}
                onClick={() => setIsGatewayInfoModalOpen(false)}
              />

              {page?.gateway?.types_info?.types?.map((type) => (
                <div className="type" key={type?.title}>
                  <strong>{type?.title}</strong>
                  <p>{type?.description}</p>
                </div>
              ))}

              <div className="footer">
                <span>{page?.gateway?.types_info?.footer_text}</span>
                <Link
                  href={page?.gateway?.types_info?.footer_button?.href!}
                  legacyBehavior
                  passHref
                >
                  <Button mSize="medium" mType="secondary">
                    {page?.gateway?.types_info?.footer_button?.title}
                  </Button>
                </Link>
              </div>
            </GatewayInfoModalContent>
          </Modal>
        </ResponsiveContainer>
      </WidthDelimiter>

      <WidthDelimiter style={{ marginTop: 100 }}>
        <ResponsiveContainer>
          <Separator />

          <SectionTitle>{page?.highlights?.title}</SectionTitle>
          <SectionText
            style={{ maxWidth: 900 }}
            dangerouslySetInnerHTML={{
              __html: page?.highlights?.text!,
            }}
          />

          {page?.highlights?.itemss?.map((item, idx) => (
            <HighlightItem
              key={item?.title}
              className={classnames({
                mirrored: !(idx % 2 === 0),
              })}
            >
              <div className="image">
                <img
                  width={535}
                  height={229}
                  src={getAssetUrl(item?.imageConnection)}
                />
              </div>

              <div className="spacer" />

              <div className="texts">
                <SectionSubTitle>{item?.title}</SectionSubTitle>
                <SectionSubText
                  dangerouslySetInnerHTML={{
                    __html: item?.text!,
                  }}
                />
              </div>
            </HighlightItem>
          ))}
        </ResponsiveContainer>
      </WidthDelimiter>

      <WidthDelimiter>
        <ResponsiveContainer>
          <Separator />

          <SectionTitle>{page?.faq?.title}</SectionTitle>

          <Accordion
            items={page?.faq?.questions?.map((q) => ({
              title: q?.question! || '',
              content: q?.answer! || '',
            }))}
          />
        </ResponsiveContainer>
      </WidthDelimiter>

      <Footer
        sections={footer?.sections}
        headline={page?.footer?.title}
        subheadline={page?.footer?.text}
        primary_button={page?.footer?.button}
      />
    </>
  );
};

export const Plan: FunctionComponent<IPlan> = ({
  title,
  description,
  infoText,
  button,
  price = '',
  priceDetails = '',
}) => {
  return (
    <PlanCard className="plan-card">
      <h2 className="title">{title}</h2>

      <div
        style={{
          height: '120px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
        }}
      >
        <h3
          className="price"
          dangerouslySetInnerHTML={{ __html: price || '' }}
        />
        <p className="term-length">{priceDetails}</p>
      </div>

      {!!infoText && <p className="text">{infoText}</p>}

      {button}

      {!!description && <p className="text">{description}</p>}
    </PlanCard>
  );
};

export async function getServerSideProps() {
  try {
    const { data: pageData } = await ContentStackClient().query<IPricingPage>({
      query: PAGE_QUERY,
    });

    return {
      props: {
        ...pageData,
      },
    };
  } catch (e) {
    return { props: {} };
  }
}

const PAGE_QUERY = gql`
  query pricingQuery {
    ${HEADER_QUERY}
    ${FOOTER_QUERY}

    page: pricing(uid: "blt44bd054a35e79931") {
      title
      description
      hero {
        title
        text
      }
      plans {
        starter {
          title
          text
          price
          price_details
          button {
            title
            href
          }
        }
        pro {
          title
          text
          price
          price_details
          button {
            title
            href
          }
        }
        enterprise {
          title
          text
          price
          price_details
          button {
            title
            href
          }
        }
        a_la_carte {
          title
          text
          price
          price_details
          button {
            title
            href
          }
        }
        quantity_text
        fess_disclaimer {
          text
          button {
            title
            href
          }
        }
      }
      gateway {
        title
        text
        types_title
        types_info {
          title
          text
          types {
            slug
            title
            description
          }
          footer_text
          footer_button {
            title
            href
          }
        }
      }
      api_credits {
        title
        text
      }
      highlights {
        title
        text
        itemss {
          title
          text
          imageConnection {
            edges {
              node {
                url
              }
            }
          }
        }
      }
      faq {
        title
        questions {
          question
          answer
        }
      }
      footer {
        title
        text
        button {
          title
          href
        }
      }
    }
  }
`;

const WidthDelimiter = styled.div`
  max-width: 1600px;
  position: relative;
  margin: 0 auto;
`;

const Hero = styled.div`
  width: 100%;
  padding-top: 160px;
  padding-bottom: 80px;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  background: url(/img/home-glow-rectangle-1.svg) no-repeat bottom right;
  background-size: 80%;
`;

const HeroTitle = styled.h1`
  color: white;
  font-size: 67px;
  font-weight: bold;
  line-height: 1.19;
  margin-top: 0;

  strong {
    color: ${(props) => props.theme.colors.teal};
  }

  ${mediaScreen('xs', 'sm')} {
    font-size: 35px;
  }

  ${mediaScreen('sm', 'md')} {
    font-size: 50px;
  }
`;

const HeroText = styled.p`
  color: white;
  font-size: 22px;
  line-height: 1.5;
  margin-top: 50px;
  max-width: 85%;
  font-weight: 400;
  margin-bottom: 44px;

  ${mediaScreen('xs', 'sm')} {
    font-size: 17px;
    max-width: 100%;
    margin-top: 24px;
    margin-bottom: 32px;
  }

  ${mediaScreen('sm', 'md')} {
    font-size: 19px;
    margin-top: 24px;
    margin-bottom: 32px;
  }
`;

const Card = styled.div`
  background: ${(props) => props.theme.colors.slate2};
  padding: 30px;
  color: white;

  .title {
    font-size: 38px;
    font-weight: 700;
    margin: 0;
  }

  .text {
    font-size: 16px;
  }

  ${mediaScreen('xs', 'md')} {
    .title {
      font-size: 29px;
    }

    .text {
      font-size: 14px;
    }
  }
`;

const Plans = styled.div`
  display: flex;
  align-items: stretch;
  justify-content: space-between;
  gap: 24px;

  ${mediaScreen('xs', 'md')} {
    display: block;
  }
`;

const PlanCard = styled(Card)`
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 30px;
  width: 30%;

  .title {
    margin-bottom: 35px;
  }

  .loading-indicator {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 83px;
  }

  .price {
    font-size: 32px;
    font-weight: 500;
    margin: 0;

    &.words {
      font-size: 30px;
      margin-top: 10px;
      margin-bottom: 9px;
    }
  }

  .term-length {
    font-size: 16px;
    font-weight: 700;
    color: ${(props) => props.theme.colors.gray};
  }

  .button {
    margin: 40px 0;
  }

  ${mediaScreen('xs', 'md')} {
    width: 100%;
    margin: 0 0 50px 0 !important;

    .title {
      margin-bottom: 20px;
    }

    .price {
      margin-bottom: 0 !important;
    }

    .term-length {
      font-size: 14px;
    }
  }
`;

const GatewayCard = styled(Card)`
  margin-top: 100px;
  margin-bottom: 50px;
  display: flex;
  padding: 50px 30px;
  align-items: stretch;
  justify-content: space-around;

  > div {
    padding-right: 5%;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  .subtitle {
    display: flex;
    margin: 0;
    font-size: 16px;
    font-weight: 700;
    margin-bottom: 10px;

    .icon {
      opacity: 0.7;
      cursor: pointer;
      margin-left: 12px;
      position: relative;
      top: 3px;

      &:hover {
        opacity: 1;
      }
    }
  }

  .text {
    margin-top: 15px;
  }

  .loading-indicator {
    display: flex;
    align-items: center;
    height: 57px;
  }

  .price {
    font-size: 30px;
    font-weight: 700;
    margin: 0;
  }

  ${mediaScreen('xs', 'md')} {
    display: block;
    padding: 30px;

    > div:not(:first-of-type):not(:last-of-type) {
      margin: 30px 0;
    }

    .text {
      margin-top: 10px;
    }

    .subtitle {
      font-size: 14px;
      margin-bottom: 5px;

      .icon {
        display: none;
      }
    }

    .price {
      font-size: 20px;
    }
  }
`;

const GatewayInfoModalContent = styled.div`
  padding: 30px;
  max-width: 700px;
  position: relative;

  h2 {
    font-size: 30px;
    margin: 0 0 7px 0;
  }

  .icon {
    position: absolute;
    right: 30px;
    top: 30px;
    cursor: pointer;

    &:hover {
      color: ${(props) => props.theme.colors.teal};
    }
  }

  .type {
    margin-top: 30px;

    &:first-of-type {
      margin-top: 50px;
    }

    strong {
      display: block;
      text-transform: capitalize;
      color: white;
    }
  }

  .footer {
    text-align: center;
    margin-top: 60px;

    .button {
      margin-left: 20px;
    }
  }
`;

const SectionTitle = styled.h2`
  font-size: 50px;
  line-height: 1.2;
  color: white;
  font-weight: bold;
  margin-top: 0;

  ${mediaScreen('xs', 'sm')} {
    font-size: 29px;
  }

  ${mediaScreen('sm', 'md')} {
    font-size: 40px;
  }
`;

const SectionSubTitle = styled.h3`
  margin-top: 0;
  margin-bottom: 25px;
  font-size: 24px;
  line-height: 1.16;
  color: white;
  font-weight: bold;

  ${mediaScreen('xs', 'sm')} {
    font-size: 18px;
    margin-bottom: 15px;
  }
`;

const SectionText = styled.p`
  color: white;
  font-size: 22px;
  line-height: 30px;

  ${mediaScreen('xs', 'sm')} {
    font-size: 17px;
    line-height: 24px;
  }

  ${mediaScreen('sm', 'md')} {
    font-size: 19px;
    line-height: 25px;
  }
`;

const SectionSubText = styled.p`
  color: white;
  font-size: 16px;
  line-height: 1.5;

  ${mediaScreen('xs', 'sm')} {
    font-size: 14px;
  }

  ${mediaScreen('sm', 'md')} {
    font-size: 16px;
  }
`;

const HighlightItem = styled.div`
  width: 100%;
  display: flex;
  padding-top: 100px;
  padding-bottom: 50px;

  .image {
    flex: 1.2;
    display: flex;
    align-items: center;
    justify-content: center;

    img {
      width: 100%;
    }
  }

  .spacer {
    flex: 0.2;
  }

  .texts {
    flex: 1;
    justify-content: center;
    display: flex;
    text-align: left;
    flex-direction: column;
  }

  &.mirrored {
    flex-direction: row-reverse;
  }

  &:last-of-type {
    margin-bottom: 100px;
  }

  ${mediaScreen('xs', 'md')} {
    flex-direction: column !important;
    padding-top: 50px;
    padding-bottom: 25px;

    &:last-of-type {
      margin-bottom: 50px;
    }

    .spacer {
      padding-top: 20px;
    }
  }
`;

interface IPlan {
  title: string;
  description?: string;
  infoText?: string;
  button?: ReactNode;
  price?: string;
  priceDetails?: string;
}

interface IPricingPage extends IPageProps<Pricing> {}

export default Page;
